import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Button,
  Stack,
} from '@mui/material';
import axios from 'axios';
import { API_URL } from './config';

/** Reliable bar chart without SVG measurement issues inside flex/grid layouts */
function HorizontalBarList({ rows, barColor, valueKey = 'value', labelKey = 'label' }) {
  const values = rows.map((r) => r[valueKey]);
  const max = Math.max(1, ...values);

  return (
    <Stack spacing={1.75}>
      {rows.map((row, index) => {
        const v = row[valueKey];
        const pct = Math.min(100, (v / max) * 100);
        return (
          <Box key={`${String(row[labelKey])}-${index}`}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                gap: 1,
                mb: 0.5,
              }}
            >
              <Typography variant="body2" sx={{ flex: '1 1 auto', minWidth: 0, pr: 1 }}>
                {row[labelKey]}
              </Typography>
              <Typography variant="body2" fontWeight={700} sx={{ flexShrink: 0, tabularNums: true }}>
                {v}
              </Typography>
            </Box>
            <Box
              sx={{
                height: 12,
                borderRadius: 1,
                bgcolor: 'rgba(148, 163, 184, 0.18)',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  width: `${pct}%`,
                  minWidth: v > 0 ? 6 : 0,
                  bgcolor: barColor,
                  borderRadius: 1,
                  transition: 'width 0.2s ease',
                }}
              />
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
}

/** Prefer human-readable names saved on the event; fall back to payload for older rows. */
function searchFolderChannelLabel(row) {
  const c = row.context || {};
  const p = row.payload || {};
  if (row.scope === 'channel') {
    return (
      c.channelTitle ||
      p.channelName ||
      p.searchSnapshot?.channelName ||
      c.channelId ||
      p.channelId ||
      '—'
    );
  }
  if (row.scope === 'folder') {
    return (
      c.folderName ||
      p.folderName ||
      p.searchSnapshot?.folderName ||
      c.folderId ||
      p.folderId ||
      '—'
    );
  }
  const names = p.folderNames;
  if (Array.isArray(names) && names.length) {
    return names.filter(Boolean).join(', ');
  }
  const ids = p.folderIds;
  if (Array.isArray(ids) && ids.length) {
    return ids.join(', ');
  }
  return 'Global';
}

function copyTitleUrlDisplay(row) {
  const p = row.payload || {};
  if (row.eventType === 'copy_url') {
    return p.url || p.copiedUrl || '—';
  }
  if (row.eventType === 'copy_title') {
    return p.copiedText || p.title || '—';
  }
  return p.title || p.url || JSON.stringify(p);
}

function AnalyticsUserDetail() {
  const { userId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [rawOffset, setRawOffset] = useState(0);
  const rawLimit = 50;

  const load = (offset = 0) => {
    setLoading(true);
    axios
      .get(`${API_URL}/api/analytics/users/${userId}`, {
        params: { rawOffset: offset, rawLimit },
      })
      .then((res) => {
        setData(res.data);
        setRawOffset(offset);
        setError('');
      })
      .catch((e) => {
        setError(e.response?.data?.error || e.message || 'Failed to load user');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  if (loading && !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !data) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  const topFolderRows =
    data?.topFolders?.map((f) => ({
      label: f.folderName || f.folderId?.slice?.(0, 8) || 'Folder',
      value: f.interactions,
    })) || [];

  const topChannelRows =
    data?.topChannels?.map((c) => ({
      label: c.channelName || c.channelId?.slice?.(0, 12) || 'Channel',
      value: c.interactions,
    })) || [];

  return (
    <Box sx={{ pb: 4, width: '100%', maxWidth: '100%' }}>
      <Button component={Link} to="/analytics" sx={{ mb: 2 }}>
        Back to overview
      </Button>

      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
        {data?.user?.username}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Role: {data?.user?.role} · User id: {data?.user?.id}
      </Typography>

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
        Top folders (interactions)
      </Typography>
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        {topFolderRows.length === 0 ? (
          <Typography color="text.secondary">No folder interactions yet.</Typography>
        ) : (
          <HorizontalBarList rows={topFolderRows} barColor="#818cf8" />
        )}
      </Paper>

      <Typography variant="h6" sx={{ mb: 2 }}>
        Top actions in top 4 folders
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
          gap: 2.5,
          mb: 3,
          width: '100%',
          alignItems: 'stretch',
        }}
      >
        {(data?.folderActionCharts || []).map((block) => {
          const actionRows =
            block.actions?.map((a) => ({
              label: a.eventType.replace(/_/g, ' '),
              value: a.count,
            })) || [];

          return (
            <Paper
              key={block.folderId}
              elevation={0}
              sx={{
                p: { xs: 2, sm: 2.5 },
                borderRadius: 2,
                width: '100%',
                minWidth: 0,
                maxWidth: '100%',
                boxSizing: 'border-box',
                border: '1px solid',
                borderColor: 'divider',
                height: '100%',
              }}
            >
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }} title={block.folderName}>
                {block.folderName || block.folderId}
              </Typography>
              {actionRows.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No actions recorded in this folder yet.
                </Typography>
              ) : (
                <HorizontalBarList rows={actionRows} barColor="#c084fc" />
              )}
            </Paper>
          );
        })}
      </Box>

      <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
        Top channels (interactions)
      </Typography>
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        {topChannelRows.length === 0 ? (
          <Typography color="text.secondary">No channel interactions yet.</Typography>
        ) : (
          <HorizontalBarList rows={topChannelRows} barColor="#38bdf8" />
        )}
      </Paper>

      <Typography variant="h6" sx={{ mb: 2 }}>
        Top actions in top 4 channels
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
          gap: 2.5,
          mb: 3,
          width: '100%',
          alignItems: 'stretch',
        }}
      >
        {(data?.channelActionCharts || []).map((block) => {
          const actionRows =
            block.actions?.map((a) => ({
              label: a.eventType.replace(/_/g, ' '),
              value: a.count,
            })) || [];

          return (
            <Paper
              key={block.channelId}
              elevation={0}
              sx={{
                p: { xs: 2, sm: 2.5 },
                borderRadius: 2,
                width: '100%',
                minWidth: 0,
                maxWidth: '100%',
                boxSizing: 'border-box',
                border: '1px solid',
                borderColor: 'divider',
                height: '100%',
              }}
            >
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }} title={block.channelName}>
                {block.channelName || block.channelId}
              </Typography>
              {actionRows.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No actions recorded for this channel yet.
                </Typography>
              ) : (
                <HorizontalBarList rows={actionRows} barColor="#f472b6" />
              )}
            </Paper>
          );
        })}
      </Box>

      <Typography variant="h6" sx={{ mb: 1 }}>
        Searches
      </Typography>
      <TableContainer component={Paper} sx={{ mb: 3, borderRadius: 2, maxHeight: 360 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Time</TableCell>
              <TableCell>Search term</TableCell>
              <TableCell>Scope</TableCell>
              <TableCell>Folder / channel</TableCell>
              <TableCell>Payload</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data?.searches || []).map((row) => (
              <TableRow key={row._id}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {row.createdAt ? new Date(row.createdAt).toLocaleString() : ''}
                </TableCell>
                <TableCell sx={{ maxWidth: 280, fontSize: 13 }}>
                  {row.payload?.query != null && String(row.payload.query).trim() !== ''
                    ? String(row.payload.query)
                    : '—'}
                </TableCell>
                <TableCell>{row.scope}</TableCell>
                <TableCell>{searchFolderChannelLabel(row)}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, maxWidth: 480 }}>
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {JSON.stringify(row.payload, null, 0)}
                  </pre>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="h6" sx={{ mb: 1 }}>
        Recent copy title / URL
      </Typography>
      <TableContainer component={Paper} sx={{ mb: 3, borderRadius: 2, maxHeight: 320 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Time</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Scope</TableCell>
              <TableCell>Title / URL</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data?.copies || []).map((row) => (
              <TableRow key={row._id}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {row.createdAt ? new Date(row.createdAt).toLocaleString() : ''}
                </TableCell>
                <TableCell>{row.eventType}</TableCell>
                <TableCell>{row.scope}</TableCell>
                <TableCell sx={{ fontSize: 12, wordBreak: 'break-word' }}>
                  {copyTitleUrlDisplay(row)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="h6" sx={{ mb: 1 }}>
        Raw events
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Total: {data?.rawTotal ?? 0}
      </Typography>
      <TableContainer component={Paper} sx={{ borderRadius: 2, maxHeight: 480 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Time</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Scope</TableCell>
              <TableCell>Payload</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data?.rawEvents || []).map((row) => (
              <TableRow key={row._id}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {row.createdAt ? new Date(row.createdAt).toLocaleString() : ''}
                </TableCell>
                <TableCell>{row.eventType}</TableCell>
                <TableCell>{row.scope}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 11, maxWidth: 520 }}>
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {JSON.stringify({ context: row.context, payload: row.payload }, null, 0)}
                  </pre>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
        <Button
          disabled={rawOffset === 0 || loading}
          onClick={() => load(Math.max(0, rawOffset - rawLimit))}
        >
          Previous
        </Button>
        <Button
          disabled={loading || !data?.rawEvents?.length || rawOffset + rawLimit >= (data?.rawTotal || 0)}
          onClick={() => load(rawOffset + rawLimit)}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
}

export default AnalyticsUserDetail;
